import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, Form, Radio } from 'src/common';
import { ContractEndSearchType } from '@tendium/prom-types';
import { Dayjs } from 'dayjs';
import dayjs from 'src/helpers/dayjs';
import { debounce, isEqual } from 'lodash';
import { useApiTenders } from 'src/models/procurements/Tenders/hooks';
import GenericSearchDropdown from '../../GenericSearchDropDown';
import { IGenericSearchDropdownProps } from '../../GenericSearchDropDown/type';
import styles from './index.module.scss';

interface FormState {
  contractEnd?: [Dayjs, Dayjs];
  contractEndSearchMode?: ContractEndSearchType;
}
const { RangePicker } = DatePicker;

const FilterContractEnd: FC<IGenericSearchDropdownProps> = props => {
  const { t } = useTranslation();
  const [{ vars: searchArgs, initVars }, updateSearchArgs] = useApiTenders();

  const [form] = Form.useForm<FormState>();

  const initialValues: FormState = useMemo(
    () => ({
      contractEnd: searchArgs.contractEnd
        ? (searchArgs.contractEnd.map(date => dayjs(date)) as [Dayjs, Dayjs])
        : undefined,
      contractEndSearchMode: searchArgs.contractEndSearchMode
    }),
    [searchArgs.contractEnd, searchArgs.contractEndSearchMode]
  );
  const onValuesChange = useMemo(
    () =>
      debounce((changedValues: Partial<FormState>) => {
        if (changedValues.contractEnd) {
          updateSearchArgs({
            contractEnd: [
              dayjs(changedValues.contractEnd[0]).utc().startOf('day').valueOf(),
              dayjs(changedValues.contractEnd[1]).utc().endOf('day').valueOf()
            ]
          });
        } else {
          updateSearchArgs({ contractEndSearchMode: changedValues.contractEndSearchMode });
        }
      }, 300),
    [updateSearchArgs]
  );

  const isChanged = useMemo(
    () =>
      !isEqual(searchArgs.contractEndSearchMode, initVars.contractEndSearchMode) || !!searchArgs.contractEnd?.length,
    [initVars.contractEndSearchMode, searchArgs.contractEnd?.length, searchArgs.contractEndSearchMode]
  );

  return (
    <GenericSearchDropdown
      {...props}
      label={t('ExpiringContracts.contractEnd')}
      overlayClassName={styles.overlay}
      isChanged={isChanged}
      overlay={
        <Form onValuesChange={onValuesChange} form={form} initialValues={initialValues}>
          <Form.Item name={'contractEnd'}>
            <RangePicker
              separator={'-'}
              allowClear={false}
              getPopupContainer={triggerNode => triggerNode.parentNode as HTMLElement}
            />
          </Form.Item>
          <Form.Item name={'contractEndSearchMode'} className={styles.radioGroup}>
            <Radio.Group className={styles.fieldGroup}>
              <Radio value={ContractEndSearchType.WithoutExtension}>
                {t('ExpiringContracts.searchModeWithoutExtension')}
              </Radio>
              <Radio value={ContractEndSearchType.WithExtension}>
                {t('ExpiringContracts.searchModeWithExtension')}
              </Radio>
              <Radio value={ContractEndSearchType.LastPossibleEndDate}>
                {t('ExpiringContracts.searchModeLastPossibleEndDate')}
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      }
    />
  );
};

export default FilterContractEnd;
